import { useState, useEffect } from 'react'
import { X, Check, Zap, Bot, Gift } from 'lucide-react'
import { getSessionSafe } from '../lib/supabase'
import useAuthStore from '../store/authStore'
import { effectivePlan } from '../config/features'
import { useT } from '../i18n/useT'
import StripeRedirectOverlay from './StripeRedirectOverlay'

export default function PremiumModal({ onClose, highlightPlan }) {
  const t = useT()
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState(null)
  const profile = useAuthStore(s => s.user)
  const userPlan = effectivePlan(profile)
  const trialAvailable = !profile?.trial_used_at && userPlan === 'free'

  // Su Android PWA standalone, window.location.href verso Stripe apre Custom Tabs
  // (la PWA va in background invece di redirezionare). Se l'utente chiude CCT
  // tornando alla PWA senza completare il pagamento, lo spinner resterebbe
  // attivo all'infinito. Resettiamo loading quando la PWA torna visibile.
  useEffect(() => {
    if (!loading) return
    const onVisibility = () => {
      if (document.visibilityState === 'visible') setLoading(false)
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [loading])

  // ── What to show based on current plan + context ──────────
  const onlyPremium = highlightPlan === 'premium' || userPlan === 'pro'
  const showPlus    = !onlyPremium && userPlan === 'free'
  const showPremium = true

  const PLUS_FEATURES = [
    { text: t('prem_plus_all_free'),    ok: true },
    { text: t('prem_plus_unlimited'),   ok: true },
    { text: t('prem_plus_voice'),       ok: true },
    { text: t('prem_plus_image'),       ok: true },
    { text: t('prem_plus_support'),     ok: true },
  ]

  const PREMIUM_FEATURES = [
    { text: t('prem_premium_all_plus'), ok: true },
    { text: t('prem_premium_agent'),    ok: true },
    { text: t('prem_premium_eisenhower'), ok: true },
    { text: t('prem_premium_voice_agent'), ok: true },
    { text: t('prem_premium_history'),  ok: true },
    { text: t('prem_premium_priority_support'), ok: true },
  ]

  const handleCheckout = async (priceId) => {
    setLoading(true)
    setError(null)
    try {
      const session = await getSessionSafe()
      if (!session) throw new Error(t('prem_error_session'))

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            priceId,
            successUrl: `${window.location.origin}/?payment=success`,
            cancelUrl:  `${window.location.origin}/?payment=cancelled`,
          }),
        }
      )
      const json = await res.json()
      if (json.error) {
        const msg = json.error === 'downgrade_not_allowed' ? t('prem_downgrade_not_allowed')
                  : json.error === 'already_on_this_plan'  ? (json.message || t('prem_error_generic'))
                  : (json.message || json.error)
        throw new Error(msg)
      }
      window.location.href = json.url
    } catch (e) {
      setError(e.message || t('prem_error_generic'))
      setLoading(false)
    }
  }

  const handleStartTrial = async () => {
    setLoading(true)
    setError(null)
    try {
      const session = await getSessionSafe()
      if (!session) throw new Error(t('prem_error_session'))

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/start-trial`,
        {
          method: 'POST',
          headers: {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            successUrl: `${window.location.origin}/?payment=success`,
            cancelUrl:  `${window.location.origin}/?payment=cancelled`,
          }),
        }
      )
      const json = await res.json()
      if (json.error) {
        const msg = json.error === 'trial_already_used' ? t('prem_trial_already_used')
                  : json.error === 'already_premium'    ? t('prem_trial_already_premium')
                  : (json.message || t('prem_error_generic'))
        throw new Error(msg)
      }
      window.location.href = json.url
    } catch (e) {
      setError(e.message || t('prem_error_generic'))
      setLoading(false)
    }
  }

  const colCount = (showPlus ? 1 : 0) + (showPremium ? 1 : 0)
  const gridClass = colCount === 2 ? 'prem-compare--2col' : 'prem-compare--1col'

  return (
    <>
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal-sheet premium-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-handle" />

        <div className="modal-header">
          <span className="modal-title">{t('prem_title')}</span>
          <button className="btn-icon" onClick={onClose} aria-label={t('close')}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {trialAvailable ? (
            // ── Stato 1: trial disponibile → singolo CTA "Prova 7 giorni" + lista feature Premium ──
            <>
              <div className="prem-trial-hero">
                <div className="prem-trial-hero-icon"><Gift size={28} strokeWidth={2} /></div>
                <h3 className="prem-trial-hero-title">{t('prem_trial_hero_title')}</h3>
                <p className="prem-trial-hero-sub">{t('prem_trial_hero_sub')}</p>
              </div>

              <div className="prem-trial-features">
                {PREMIUM_FEATURES.map((f, i) => (
                  <div key={i} className="prem-feat">
                    <span className="prem-feat-icon"><Check size={13} strokeWidth={3} /></span>
                    {f.text}
                  </div>
                ))}
              </div>

              <button
                className="btn btn-primary prem-trial-cta-big"
                onClick={handleStartTrial}
                disabled={loading}
              >
                {loading ? t('prem_redirecting') : t('prem_trial_cta_big')}
              </button>

              {error && <div className="prem-error">{error}</div>}

              <p className="prem-fine">{t('prem_trial_fine_print')}</p>
            </>
          ) : (
            // ── Stato 2: trial già usato o utente con piano attivo → confronto piani con bottoni acquisto ──
            <>
              <div className={`prem-compare ${gridClass}`}>
                {showPlus && (
                  <div className="prem-col prem-col--plus">
                    <div className="prem-col-header">
                      <Zap size={14} strokeWidth={2.5} style={{ marginRight: 4 }} />
                      Plus
                    </div>
                    <div className="prem-col-price">
                      <span className="prem-price-amount">{t('prem_price_plus')}</span>
                      <span className="prem-price-period">/{t('prem_year')}</span>
                    </div>
                    {PLUS_FEATURES.map((f, i) => (
                      <div key={i} className="prem-feat">
                        <span className="prem-feat-icon">
                          <Check size={13} strokeWidth={3} />
                        </span>
                        {f.text}
                      </div>
                    ))}
                    <button
                      className="btn btn-secondary prem-cta"
                      onClick={() => handleCheckout(import.meta.env.VITE_STRIPE_PLUS_PRICE_ID)}
                      disabled={loading}
                    >
                      {loading ? t('prem_redirecting') : t('prem_cta_plus')}
                    </button>
                  </div>
                )}

                {showPremium && (
                  <div className="prem-col prem-col--premium prem-col--highlight">
                    <div className="prem-col-header">
                      <Bot size={14} strokeWidth={2.5} style={{ marginRight: 4 }} />
                      Premium
                    </div>
                    <div className="prem-col-price">
                      <span className="prem-price-amount">{t('prem_price_premium')}</span>
                      <span className="prem-price-period">/{t('prem_year')}</span>
                    </div>
                    {PREMIUM_FEATURES.map((f, i) => (
                      <div key={i} className="prem-feat">
                        <span className="prem-feat-icon">
                          <Check size={13} strokeWidth={3} />
                        </span>
                        {f.text}
                      </div>
                    ))}
                    <button
                      className="btn btn-primary prem-cta"
                      onClick={() => handleCheckout(import.meta.env.VITE_STRIPE_PREMIUM_PRICE_ID)}
                      disabled={loading}
                    >
                      {loading ? t('prem_redirecting') : t('prem_cta_premium')}
                    </button>
                  </div>
                )}
              </div>

              {error && <div className="prem-error">{error}</div>}

              <p className="prem-fine">{t('prem_fine_print')}</p>
            </>
          )}
        </div>
      </div>
    </div>
    {loading && <StripeRedirectOverlay />}
    </>
  )
}
